import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import BackToTop from '@/components/base/BackToTop';
import { mangaData } from '@/mocks/manga';

export default function MangaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [reading, setReading] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(0);

  const manga = mangaData.find((m) => m.id === Number(id));

  if (!manga) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">抱歉，该漫画不存在或已下架</p>
            <Link
              to="/category/all"
              className="px-6 py-2.5 text-sm font-medium bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回漫画列表
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (reading) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-950">
          <div className="sticky top-0 z-50 bg-background-900 border-b border-background-700/50">
            <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
              <button
                onClick={() => setReading(false)}
                className="flex items-center gap-1 text-sm text-background-300 hover:text-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回详情
              </button>
              <span className="text-sm text-background-300 font-medium">
                {manga.chapters[currentChapter]?.title}
              </span>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setCurrentChapter(Math.max(0, currentChapter - 1))}
                  disabled={currentChapter === 0}
                  className="text-sm text-background-300 hover:text-background-100 transition-colors disabled:opacity-30 cursor-pointer"
                >
                  <i className="ri-arrow-up-s-line"></i> 上一话
                </button>
                <button
                  onClick={() => setCurrentChapter(Math.min(manga.chapters.length - 1, currentChapter + 1))}
                  disabled={currentChapter === manga.chapters.length - 1}
                  className="text-sm text-background-300 hover:text-background-100 transition-colors disabled:opacity-30 cursor-pointer"
                >
                  下一话 <i className="ri-arrow-down-s-line"></i>
                </button>
              </div>
            </div>
          </div>
          <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-background-800 rounded-lg overflow-hidden aspect-[16/9] flex items-center justify-center"
                >
                  <div className="text-center">
                    <div className="w-20 h-20 flex items-center justify-center mx-auto mb-3 bg-background-700 rounded-full">
                      <i className="ri-image-line text-3xl text-background-500"></i>
                    </div>
                    <p className="text-sm text-background-500">{manga.chapters[currentChapter]?.title} - 第{i + 1}页</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-8 pb-8">
              <button
                onClick={() => setCurrentChapter(Math.max(0, currentChapter - 1))}
                disabled={currentChapter === 0}
                className="px-5 py-2.5 text-sm font-medium bg-background-800 text-background-300 rounded-full hover:bg-background-700 transition-colors disabled:opacity-30 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-s-line mr-1"></i>上一话
              </button>
              <button
                onClick={() => setReading(false)}
                className="px-5 py-2.5 text-sm font-medium bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回目录
              </button>
              <button
                onClick={() => setCurrentChapter(Math.min(manga.chapters.length - 1, currentChapter + 1))}
                disabled={currentChapter === manga.chapters.length - 1}
                className="px-5 py-2.5 text-sm font-medium bg-background-800 text-background-300 rounded-full hover:bg-background-700 transition-colors disabled:opacity-30 cursor-pointer whitespace-nowrap"
              >
                下一话<i className="ri-arrow-right-s-line ml-1"></i>
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const relatedManga = mangaData.filter((m) => m.category === manga.category && m.id !== manga.id).slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: `${manga.category}漫画`, path: `/category/${manga.category === '玄幻' ? 'xuan-huan' : manga.category === '武侠' ? 'wu-xia' : manga.category === '古风' ? 'gu-feng' : manga.category === '恋爱' ? 'lian-ai' : manga.category === '悬疑' ? 'xuan-yi' : manga.category === '科幻' ? 'ke-huan' : manga.category === '热血' ? 're-xue' : manga.category === '搞笑' ? 'gao-xiao' : manga.category === '日常' ? 'ri-chang' : 'all'}` },
                { label: manga.title },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 w-full md:w-64">
                <div className="rounded-lg overflow-hidden aspect-[3/4]">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {manga.title}
                </h1>
                <p className="text-sm text-foreground-700 mb-2">
                  <strong>作者：</strong>{manga.author}
                </p>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`px-2.5 py-0.5 text-xs font-medium rounded-md ${
                    manga.status === '连载中'
                      ? 'bg-accent-100 text-accent-900'
                      : 'bg-secondary-100 text-secondary-900'
                  }`}>
                    {manga.status}
                  </span>
                  <span className="text-xs text-foreground-500">{manga.category}</span>
                  <span className="text-xs text-foreground-500">|</span>
                  <span className="text-xs text-accent-600 font-medium">
                    <i className="ri-star-fill mr-0.5"></i>{manga.rating}分
                  </span>
                  <span className="text-xs text-foreground-500">|</span>
                  <span className="text-xs text-foreground-500">{manga.views}次阅读</span>
                  <span className="text-xs text-foreground-500">|</span>
                  <span className="text-xs text-primary-600">{manga.latestChapter}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {manga.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-secondary-100 text-secondary-900 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                  {manga.description}
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setReading(true)}
                    className="px-6 py-3 text-sm font-bold bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-line mr-1.5"></i>开始阅读
                  </button>
                  <button
                    onClick={() => {
                      setCurrentChapter(manga.chapters.length - 1);
                      setReading(true);
                    }}
                    className="px-6 py-3 text-sm font-medium bg-background-100 text-foreground-800 rounded-full hover:bg-primary-50 hover:text-primary-600 transition-all cursor-pointer whitespace-nowrap border border-background-200/70"
                  >
                    从最新话开始
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center bg-background-100 text-foreground-600 rounded-full hover:bg-accent-50 hover:text-accent-600 transition-all cursor-pointer border border-background-200/70">
                    <i className="ri-heart-line"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center bg-background-100 text-foreground-600 rounded-full hover:bg-secondary-50 hover:text-secondary-600 transition-all cursor-pointer border border-background-200/70">
                    <i className="ri-bookmark-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
              章节目录
              <span className="text-sm font-normal text-foreground-500 ml-2">共 {manga.chapters.length} 话</span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1">
              {manga.chapters.map((chapter, index) => (
                <button
                  key={chapter.id}
                  onClick={() => {
                    setCurrentChapter(index);
                    setReading(true);
                  }}
                  className="flex items-center justify-between px-4 py-3 text-sm bg-background-50 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer text-left"
                >
                  <span className="text-foreground-800">{chapter.title}</span>
                  <span className="text-xs text-foreground-500 flex-shrink-0 ml-4">{chapter.time}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {relatedManga.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                {relatedManga.map((rm) => (
                  <Link
                    key={rm.id}
                    to={`/manga/${rm.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                      <img
                        src={rm.cover}
                        alt={rm.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground-950/80 to-transparent p-3">
                        <span className="text-xs text-background-100">{rm.latestChapter}</span>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rm.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{rm.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}